import { useEffect, useId, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, X } from 'lucide-react';
import Popover from '../ui/Popover';
import { announce } from '../ui/LiveAnnouncer';
import { additionalOptions, type AssignableAgent } from './taskAssigneesModel';
import EntityPicker from '../ui/EntityPicker';

/**
 * "בעלים" + "משויכים נוספים" for every task-creation form (owner decision
 * 2026-10-06: a task I created is a task I own; anyone can add more people).
 *
 * - Owner: the creator as a fixed chip "‹name› (אני)" when they are assignable
 *   for the organization (`creatorOwner`); otherwise one quiet line says so and
 *   a select lets the user pick the owner (left empty, the first additional
 *   person becomes the owner — `resolveTaskAssignees`).
 * - Additional assignees: an ARIA 1.2 multi-select combobox (input + listbox in
 *   `ui/Popover`), chosen people as removable chips, never the owner.
 *
 * Presentation only: the parent keeps the state and builds the payload with
 * `resolveTaskAssignees`, so the API call keeps its shape.
 */
export interface TaskAssigneesFieldProps {
  agents: readonly AssignableAgent[];
  /** The assignable-agents query is still loading. */
  loading?: boolean;
  /** The creator as fixed owner, or null (fallback: pick the owner). */
  creatorOwner: AssignableAgent | null;
  pickedOwnerId: string;
  onPickedOwnerChange: (id: string) => void;
  additionalIds: readonly string[];
  onAdditionalChange: (ids: string[]) => void;
  /** Mark the owner as required (`*`), e.g. when the form demands an assignee. */
  ownerRequired?: boolean;
}

export default function TaskAssigneesField({
  agents,
  loading = false,
  creatorOwner,
  pickedOwnerId,
  onPickedOwnerChange,
  additionalIds,
  onAdditionalChange,
  ownerRequired = false,
}: TaskAssigneesFieldProps) {
  const { t } = useTranslation();
  const baseId = useId();
  const ownerLabelId = `${baseId}-owner`;
  const ownerSelectId = `${baseId}-owner-select`;
  const ownerHintId = `${baseId}-owner-hint`;

  if (loading) {
    return <p className="text-sm text-text-2" aria-live="polite">{t('taskAssignees.loading')}</p>;
  }
  if (agents.length === 0) {
    return <p className="text-sm text-text-2">{t('project.noAgentsAvailable')}</p>;
  }

  const ownerId = creatorOwner?.id ?? pickedOwnerId;
  const required = ownerRequired && <span className="text-danger-700" aria-hidden="true"> *</span>;

  return (
    <div className="space-y-4">
      {creatorOwner ? (
        <div role="group" aria-labelledby={ownerLabelId} className="space-y-1.5">
          <p id={ownerLabelId} className="text-sm font-medium text-text-2">{t('taskAssignees.owner')}</p>
          <p className="inline-flex max-w-full items-center gap-2 rounded-full bg-accent-soft py-1 pe-3 ps-1 text-sm text-accent-soft-text">
            <Initial name={creatorOwner.name} tone="owner" />
            <span className="truncate">{t('taskAssignees.ownerMe', { name: creatorOwner.name })}</span>
          </p>
        </div>
      ) : (
        <div className="space-y-1.5">
          <label htmlFor={ownerSelectId} className="block text-sm font-medium text-text-2">
            {t('taskAssignees.owner')}{required}
          </label>
          <EntityPicker
            id={ownerSelectId}
            aria-describedby={ownerHintId}
            placeholder={t('taskAssignees.selectOwner')}
            options={agents.map((a) => ({ value: a.id, label: a.name }))}
            value={pickedOwnerId}
            onChange={(id) => {
              onPickedOwnerChange(id);
              // The owner is never also an additional assignee.
              if (id && additionalIds.includes(id)) onAdditionalChange(additionalIds.filter((x) => x !== id));
            }}
          />
          <p id={ownerHintId} className="text-xs text-text-2">{t('taskAssignees.creatorNotAssignable')}</p>
        </div>
      )}

      <AdditionalAssigneesCombobox
        agents={agents}
        ownerId={ownerId}
        selectedIds={additionalIds}
        onChange={onAdditionalChange}
      />
    </div>
  );
}

// ─── Initial avatar ──────────────────────────────────────────────────────────

function Initial({ name, tone = 'plain' }: { name: string; tone?: 'owner' | 'plain' }) {
  const letter = (name.trim()[0] ?? '?').toLocaleUpperCase();
  const cls = tone === 'owner' ? 'bg-accent text-on-accent' : 'bg-surface-subtle text-text-2';
  return (
    <span className={`flex size-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold ${cls}`} aria-hidden="true">
      {letter}
    </span>
  );
}

// ─── Multi-select combobox ───────────────────────────────────────────────────

/** P-06/A — also the task form's "עובדים משויכים" (with its own `label`, no owner). */
export function AdditionalAssigneesCombobox({
  agents,
  ownerId,
  selectedIds,
  onChange,
  label,
}: {
  agents: readonly AssignableAgent[];
  ownerId: string;
  selectedIds: readonly string[];
  onChange: (ids: string[]) => void;
  label?: string;
}) {
  const { t } = useTranslation();
  const baseId = useId();
  const labelId = `${baseId}-label`;
  const inputId = `${baseId}-input`;
  const listId = `${baseId}-list`;
  const optionId = (i: number) => `${baseId}-opt-${i}`;
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(-1);

  const chosen = selectedIds
    .filter((id) => id !== ownerId)
    .map((id) => agents.find((a) => a.id === id))
    .filter((a): a is AssignableAgent => !!a);
  const options = additionalOptions(agents, ownerId, query);
  const isChosen = (id: string) => selectedIds.includes(id);

  // Keep the active option in range and in view.
  useEffect(() => {
    if (active >= options.length) setActive(options.length - 1);
  }, [active, options.length]);
  useEffect(() => {
    if (!open || active < 0) return;
    document.getElementById(optionId(active))?.scrollIntoView({ block: 'nearest' });
    // optionId is derived from baseId, which is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, active]);

  const toggle = (agent: AssignableAgent) => {
    if (isChosen(agent.id)) {
      onChange(selectedIds.filter((id) => id !== agent.id));
      announce(t('taskAssignees.removed', { name: agent.name }));
    } else {
      onChange([...selectedIds.filter((id) => id !== ownerId), agent.id]);
      announce(t('taskAssignees.added', { name: agent.name }));
    }
    setQuery('');
  };

  const remove = (agent: AssignableAgent) => {
    onChange(selectedIds.filter((id) => id !== agent.id));
    announce(t('taskAssignees.removed', { name: agent.name }));
    inputRef.current?.focus();
  };

  const openList = () => {
    setOpen(true);
    setActive((a) => (a < 0 && options.length > 0 ? 0 : a));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) { openList(); return; }
        setActive((a) => Math.min(a + 1, options.length - 1));
        return;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) { openList(); return; }
        setActive((a) => Math.max(a - 1, 0));
        return;
      case 'Home':
      case 'End':
        if (!open || options.length === 0) return;
        e.preventDefault();
        setActive(e.key === 'Home' ? 0 : options.length - 1);
        return;
      case 'Enter':
        // Inside an open list, Enter picks; it never submits the form.
        if (!open) return;
        e.preventDefault();
        if (active >= 0 && options[active]) toggle(options[active]);
        return;
      case 'Escape':
        // Close the list (or clear the text) first; the dialog closes on the next Escape.
        if (open) { e.preventDefault(); setOpen(false); return; }
        if (query) { e.preventDefault(); setQuery(''); }
        return;
      case 'Backspace':
        if (!query && chosen.length > 0) remove(chosen[chosen.length - 1]);
        return;
      case 'Tab':
        setOpen(false);
        return;
      default:
    }
  };

  const pickFromEvent = (e: MouseEvent<HTMLElement>) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-index]');
    if (!el) return;
    const agent = options[Number(el.dataset.index)];
    if (agent) toggle(agent);
    inputRef.current?.focus();
  };
  const moveFromEvent = (e: MouseEvent<HTMLElement>) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-index]');
    if (el) setActive(Number(el.dataset.index));
  };

  return (
    <div className="space-y-1.5">
      <label id={labelId} htmlFor={inputId} className="block text-sm font-medium text-text-2">
        {label ?? t('taskAssignees.additional')}
      </label>
      <div
        ref={anchorRef}
        className="flex min-h-11 w-full flex-wrap items-center gap-1.5 rounded-lg border border-control bg-surface py-1.5 pe-1 ps-2 transition-[border-color,box-shadow] duration-150 hover:border-surface-600 focus-within:border-focus focus-within:ring-1 focus-within:ring-focus"
      >
        {chosen.length > 0 && (
          <ul className="contents" aria-label={t('taskAssignees.chosenList')}>
            {chosen.map((a) => (
              <li key={a.id} className="inline-flex max-w-full items-center gap-1 rounded-full bg-surface-subtle py-0.5 pe-0.5 ps-1 text-sm text-text">
                <Initial name={a.name} />
                <span className="truncate">{a.name}</span>
                <button
                  type="button"
                  onClick={() => remove(a)}
                  aria-label={t('taskAssignees.remove', { name: a.name })}
                  className="flex size-6 flex-shrink-0 items-center justify-center rounded-full text-text-2 hover:bg-surface-200 hover:text-text"
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && active >= 0 && options[active] ? optionId(active) : undefined}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
          onClick={openList}
          onKeyDown={onKeyDown}
          placeholder={t('taskAssignees.placeholder')}
          autoComplete="off"
          spellCheck={false}
          className="min-w-32 flex-1 bg-transparent py-1 text-sm text-text placeholder:text-text-3 focus:outline-none"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => { if (open) setOpen(false); else { openList(); inputRef.current?.focus(); } }}
          aria-label={t('taskAssignees.toggleList')}
          aria-expanded={open}
          aria-controls={listId}
          className="flex size-8 flex-shrink-0 items-center justify-center rounded-md text-text-2 hover:bg-surface-subtle hover:text-text"
        >
          <ChevronDown size={16} strokeWidth={1.75} aria-hidden="true" className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        align="start"
        className="w-72 p-1.5"
      >
        <ul
          id={listId}
          role="listbox"
          aria-labelledby={labelId}
          aria-multiselectable="true"
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()}
          onClick={pickFromEvent}
          onMouseMove={moveFromEvent}
          onKeyDown={onKeyDown}
          className="max-h-64 overflow-y-auto overscroll-contain focus:outline-none"
        >
          {options.map((a, i) => {
            const selected = isChosen(a.id);
            const current = i === active;
            return (
              <li
                key={a.id}
                id={optionId(i)}
                role="option"
                aria-selected={selected}
                data-index={i}
                className={`flex cursor-default items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm ${current ? 'bg-accent-soft text-accent-soft-text' : 'text-text'}`}
              >
                <Initial name={a.name} />
                <span className="min-w-0 flex-1">
                  <span className={`block truncate ${selected ? 'font-medium' : ''}`}>{a.name}</span>
                  {a.is_account_manager && (
                    <span className={`block truncate text-xs ${current ? 'text-accent-soft-text' : 'text-text-2'}`}>{t('common.accountManager')}</span>
                  )}
                </span>
                {selected && <Check size={16} className="flex-shrink-0" aria-hidden="true" />}
              </li>
            );
          })}
        </ul>
        {options.length === 0 && (
          <p className="px-2.5 py-3 text-sm text-text-2">
            {query ? t('taskAssignees.noMatches') : t('taskAssignees.noneLeft')}
          </p>
        )}
      </Popover>
    </div>
  );
}
